import { Button, ButtonColor, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { useRef, useState } from "react";
import Notifications from "util/notifications";
import { useAutomationsApi } from "./hooks";
import type { ApprovalIds, ApprovalsQuery } from "./types";
import { approveAllWords, approvedWords, leftOutWords } from "./words";

/**
 * Approve all, on Waiting for approval's filter row: every item the search
 * and the pills keep, on every page, but those held because details could
 * not be filled in, which a person looks at one by one (it says how many,
 * when some are). Its confirm opens with what the backend reads then, so
 * what it counts is what it approves; approving goes a batch at a time, the
 * button saying how far along it is, and any way of closing it stops after
 * the batch going. The toast says how it ended, and the list and the count
 * of what waits read again.
 */
export default function ActivityApproveAll({
  query,
  total,
  leftOut,
  disabled,
}: {
  query: ApprovalsQuery;
  /** How many it would approve, as the list says. */
  total: number;
  /** How many it leaves out, as the list says. */
  leftOut: number;
  /** The list is still loading, so the counts may be out of date. */
  disabled: boolean;
}) {
  const api = useAutomationsApi();
  const [reading, setReading] = useState(false);
  // What the confirm asks about, while it is open.
  const [asked, setAsked] = useState<ApprovalIds | null>(null);
  // How many are answered once it is going.
  const [answered, setAnswered] = useState<number | null>(null);
  const [stopping, setStopping] = useState(false);
  const stop = useRef(false);
  const going = answered !== null;

  const openConfirm = () => {
    setReading(true);
    api
      .listApprovalIds(query)
      .then((found) => {
        if (found.ids.length) setAsked(found);
        else Notifications.info("Nothing is left to approve.");
      })
      .catch((e) =>
        Notifications.error(
          errorText(e, "What is waiting could not be counted.")
        )
      )
      .finally(() => setReading(false));
  };

  const closeOrStop = () => {
    if (going) {
      stop.current = true;
      setStopping(true);
    } else setAsked(null);
  };

  const approve = () => {
    if (!asked || going) return;
    const { ids } = asked;
    stop.current = false;
    setAnswered(0);
    api
      .approveAll(ids, {
        onProgress: setAnswered,
        stopped: () => stop.current,
      })
      .then((result) => {
        const said = approvedWords(result);
        const left =
          ids.length -
          result.approved -
          result.skipped -
          result.failed -
          result.taken;
        if (result.problem)
          Notifications.error(
            `${said} It stopped there: ${result.problem} The rest are still waiting.`
          );
        else if (left > 0)
          Notifications.info(`${said} The other ${left} are still waiting.`);
        else if (result.failed) Notifications.error(said);
        else Notifications.success(said);
      })
      .finally(() => {
        setAnswered(null);
        setStopping(false);
        setAsked(null);
      });
  };

  return (
    <div className="flex items-center gap-3">
      {leftOut > 0 && (
        <span className="text-xs text-gray-500">{leftOutWords(leftOut)}</span>
      )}
      <Button
        color={ButtonColor.GREEN}
        size={ButtonSize.SMALL}
        disabled={disabled || reading}
        onClick={openConfirm}
      >
        Approve all ({total})
      </Button>
      <Dialog
        open={asked !== null}
        onOpenChange={(next) => !next && closeOrStop()}
      >
        {asked && (
          <DialogContent className="dialog-content">
            <h2 className="mb-2 text-xl font-semibold">
              Approve {asked.ids.length}?
            </h2>
            <p className="text-sm text-gray-700">
              {approveAllWords(asked.kinds)}
              {asked.leftOut > 0 && ` ${leftOutWords(asked.leftOut)}.`}
            </p>
            <DialogActions>
              <DialogAction
                primary={true}
                color={ButtonColor.GREEN}
                disabled={going}
                onClick={approve}
              >
                {going
                  ? `Approving ${answered} of ${asked.ids.length}…`
                  : `Approve ${asked.ids.length}`}
              </DialogAction>
              <DialogAction
                color={ButtonColor.SKYBLUE}
                disabled={stopping}
                onClick={closeOrStop}
              >
                {going ? (stopping ? "Stopping…" : "Stop") : "Cancel"}
              </DialogAction>
            </DialogActions>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
